/**
 * 顶层业务模块定义
 *
 * 登录后先进入「模块首页」选择模块，再进入对应模块工作区。
 * 三个模块：
 *  - tender-doc     招标文件编写（已实现，招标方视角：编制招标/采购文件）
 *  - bid-screening  招标信息筛选（规划中，投标方视角：结合公司资质判断这个标是否值得投）
 *  - bid-writing    投标文件编写（已实现，即原有标书解析 → 目录编辑 → 正文编辑链路）
 */

export type AppModuleId = 'tender-doc' | 'bid-screening' | 'bid-writing';

export type AppModuleStatus = 'ready' | 'planned';

export interface AppModuleMeta {
  id: AppModuleId;
  /** 模块名称（登录后模块首页的标题） */
  name: string;
  /** 一句话说明该模块面向的角色和产出 */
  summary: string;
  /** 面向的使用方 */
  audience: string;
  status: AppModuleStatus;
  statusLabel: string;
  /** 模块能力清单：ready 为已具备能力，planned 为规划能力 */
  capabilities: string[];
}

export const APP_MODULES: AppModuleMeta[] = [
  {
    id: 'tender-doc',
    name: '招标文件编写',
    summary: '面向招标方，按模板策划并编制招标（采购）文件，辅助价格与资质条款设置。',
    audience: '招标方 / 招标代理',
    status: 'ready',
    statusLabel: '已实现',
    capabilities: [
      '内置模板：工程公开招标 / 货物公开招标 / 服务竞争性磋商，按采购方式与评标办法调整章节口径',
      '立项信息：采购方式、预算与最高限价、包件、评标办法与价格分、资格条件、保证金与法定时限',
      '按分部编制：招标公告、投标人须知、评标办法、合同条款、技术规格书、格式附件',
      '逐章流式编制 + 人工编辑，正文与目录复用标书链路（可保存、可重编）',
      'Word 导出：全套章节导出，支持选择版式模板',
      '规划中：条款库与价格参考库、合规性审核（法规与集团制度）、历史招标文件解析成模板',
    ],
  },
  {
    id: 'bid-screening',
    name: '招标信息筛选',
    summary: '面向投标方，录入招标公告并对标企业资质与业绩，判断这个标是否值得投、由哪家企业去投。',
    audience: '投标方 / 市场（商务）人员',
    status: 'ready',
    statusLabel: '已实现',
    capabilities: [
      '招标信息录入：上传公告文件、粘贴文本、网址抓取（含正文预览）',
      '公告要点抽取：项目信息、资格条件、评分办法与逐条要求清单（含评分项分值）',
      '资质门槛比对：对照企业信息库的资质附件与财务数据逐条判定',
      '资质对比与决策：任选 1~6 家企业，逐家给出「适合投标 / 谨慎投标 / 不适合投标」的结论，多家时排名并推荐投标企业',
      '人工复核：可修正单条判定与硬性门槛标记，确认结果直接用于「资质对比与决策」',
      '转入投标文件编写：公告正文随项目带入标书解析链路',
    ],
  },
  {
    id: 'bid-writing',
    name: '投标文件编写',
    summary: '面向投标方，解析招标文件并生成目录与正文，产出投标文件。',
    audience: '投标方 / 标书编写人员',
    status: 'ready',
    statusLabel: '已实现',
    capabilities: [
      '标书解析：上传招标文件并提取技术要求、评分标准',
      '目录编辑：技术部分与商务部分目录生成与调整',
      '正文编辑：按目录生成、编辑、导出投标文件正文',
      '知识库、企业信息库、模型配置',
      '工具集：标书查重、标书评审、Word 导出',
    ],
  },
];

/** 默认模块（现有功能，未选择模块时用于兜底） */
export const DEFAULT_MODULE_ID: AppModuleId = 'bid-writing';

export function getModuleMeta(id: AppModuleId): AppModuleMeta {
  const found = APP_MODULES.find((item) => item.id === id);
  if (found) {
    return found;
  }
  return APP_MODULES[APP_MODULES.length - 1];
}

export function isValidModuleId(value: string | null | undefined): value is AppModuleId {
  return !!value && APP_MODULES.some((item) => item.id === value);
}
